<template>
    <div class="relative min-h-screen w-full flex bg-gradient-to-br from-gray-100 to-blue-50">
        <div :class="hideBreadcrumb ? 'p-0' : 'container mx-auto px-4 sm:px-8 py-6'">
            <!-- Breadcrumb -->
            <nav v-if="!hideBreadcrumb" class="text-sm text-gray-600 mb-6 flex">
                <router-link to="/dashboard" class="hover:text-blue-700 transition font-medium">Dashboard</router-link>
                <span class="mx-1">/</span>
                <span class="text-blue-900 font-semibold">Policies, Processes, and Procedures</span>
            </nav>

            <!-- Header -->
            <div class="header-section bg-white shadow-sm rounded-lg p-6 mb-6">
                <div class="flex items-center justify-between">
                    <div>
                        <h1 class="text-2xl font-bold text-gray-900 mb-2">Policies, Processes, and Procedures Management
                        </h1>
                        <p v-if="hasPermission('policies-processes-and-procedures-create')" class="text-gray-600">Upload
                            and manage policies,
                            processes, and
                            procedures for
                            easy access</p>
                    </div>
                    <div v-if="hasPermission('policies-processes-and-procedures-create')" class="flex space-x-3">
                        <base-button @click="showUploadModal = true" class="bg-blue-600 hover:bg-blue-700 text-white">
                            <font-awesome-icon icon="upload" class="mr-2" />
                            Upload Document
                        </base-button>
                    </div>
                </div>
            </div>

            <!-- Statistics Cards -->
            <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                <base-card class="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
                    <div class="flex items-center">
                        <div class="p-3 bg-white bg-opacity-20 rounded-lg">
                            <font-awesome-icon icon="file-alt" class="text-2xl" />
                        </div>
                        <div class="ml-4">
                            <p class="text-sm opacity-90">Total Documents</p>
                            <p class="text-2xl font-bold">{{ statistics.total_guides || 0 }}</p>
                        </div>
                    </div>
                </base-card>

                <base-card class="bg-gradient-to-r from-green-500 to-green-600 text-white">
                    <div class="flex items-center">
                        <div class="p-3 bg-white bg-opacity-20 rounded-lg">
                            <font-awesome-icon icon="hdd" class="text-2xl" />
                        </div>
                        <div class="ml-4">
                            <p class="text-sm opacity-90">Total Size</p>
                            <p class="text-2xl font-bold">{{ statistics.formatted_total_size || '0 B' }}</p>
                        </div>
                    </div>
                </base-card>

                <base-card class="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
                    <div class="flex items-center">
                        <div class="p-3 bg-white bg-opacity-20 rounded-lg">
                            <font-awesome-icon icon="file-pdf" class="text-2xl" />
                        </div>
                        <div class="ml-4">
                            <p class="text-sm opacity-90">PDF Files</p>
                            <p class="text-2xl font-bold">{{ getFileTypeCount('application/pdf') }}</p>
                        </div>
                    </div>
                </base-card>

                <base-card class="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
                    <div class="flex items-center">
                        <div class="p-3 bg-white bg-opacity-20 rounded-lg">
                            <font-awesome-icon icon="file-word" class="text-2xl" />
                        </div>
                        <div class="ml-4">
                            <p class="text-sm opacity-90">Word Files</p>
                            <p class="text-2xl font-bold">{{ getWordFileCount() }}</p>
                        </div>
                    </div>
                </base-card>
            </div>

            <!-- Search and Filters -->
            <div class="bg-white shadow-sm rounded-lg p-6 mb-6">
                <div class="flex flex-col md:flex-row gap-4">
                    <div class="flex-1">
                        <label class="block text-sm font-medium text-gray-700 mb-2">Search Documents</label>
                        <input v-model="searchQuery" type="text"
                            placeholder="Search by name, description, or filename..."
                            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            @input="debounceSearch" />
                    </div>
                    <div class="md:w-48">
                        <label class="block text-sm font-medium text-gray-700 mb-2">File Type</label>
                        <select v-model="selectedFileType"
                            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            @change="fetchGuides">
                            <option value="">All Types</option>
                            <option value="application/pdf">PDF</option>
                            <option value="application/msword">Word (.doc)</option>
                            <option value="application/vnd.openxmlformats-officedocument.wordprocessingml.document">Word
                                (.docx)
                            </option>
                            <option value="application/vnd.ms-excel">Excel (.xls)</option>
                            <option value="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet">Excel
                                (.xlsx)</option>
                            <option value="application/vnd.ms-powerpoint">PowerPoint (.ppt)</option>
                            <option value="application/vnd.openxmlformats-officedocument.presentationml.presentation">
                                PowerPoint
                                (.pptx)
                            </option>
                            <option value="text/plain">Text</option>
                            <option value="image/jpeg">Image (JPEG)</option>
                            <option value="image/png">Image (PNG)</option>
                        </select>
                    </div>
                    <div class="md:w-48">
                        <label class="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
                        <select v-model="sortBy"
                            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            @change="fetchGuides">
                            <option value="created_at">Date Created</option>
                            <option value="name">Name</option>
                            <option value="file_size">File Size</option>
                            <option value="file_type">File Type</option>
                        </select>
                    </div>
                </div>
            </div>

            <!-- Guides List -->
            <div class="bg-white shadow-sm rounded-lg">
                <div class="p-6 border-b border-gray-200">
                    <h2 class="text-lg font-semibold text-gray-900">Policies, Processes, and Procedures</h2>
                </div>

                <!-- Loading State -->
                <div class="h-full flex justify-center items-center" v-if="loading">
                    <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
                </div>

                <!-- Empty State -->
                <div v-else-if="guides.length === 0" class="p-8 text-center">
                    <div class="text-gray-400 mb-4">
                        <font-awesome-icon icon="folder-open" class="text-6xl" />
                    </div>
                    <h3 class="text-lg font-medium text-gray-900 mb-2">No documents found</h3>
                    <p class="text-gray-600 mb-4">
                        {{ emptyStateText }}
                    </p>
                    <base-button
                        v-if="hasPermission('policies-processes-and-procedures-create') && !searchQuery && !selectedFileType"
                        @click="showUploadModal = true" class="bg-blue-600 hover:bg-blue-700 text-white">
                        <font-awesome-icon icon="upload" class="mr-2" />
                        Upload First Document
                    </base-button>
                </div>

                <!-- Documents Grid -->
                <div v-else class="p-6">
                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div v-for="guide in guides" :key="guide.id"
                            class="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg hover:border-blue-300 transition-all duration-300 flex flex-col group">
                            <!-- File Icon and Menu -->
                            <div class="flex items-center justify-between mb-4">
                                <div class="flex items-center">
                                    <div
                                        class="p-4 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl shadow-sm group-hover:shadow-md transition-shadow">
                                        <font-awesome-icon :icon="getFileIcon(guide.file_type)"
                                            :class="getFileIconColor(guide.file_type)" class="text-2xl" />
                                    </div>
                                    <div class="ml-4">
                                        <p class="text-sm font-medium text-gray-700">{{
                                            guide.file_extension.toUpperCase() }}</p>
                                        <p class="text-xs text-gray-500">{{ guide.formatted_file_size }}</p>
                                    </div>
                                </div>
                                <div v-if="hasPermission('policies-processes-and-procedures-update') || hasPermission('policies-processes-and-procedures-delete')"
                                    class="relative">
                                    <button @click="showGuideMenu(guide.id)"
                                        class="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
                                        <font-awesome-icon icon="ellipsis-v" />
                                    </button>
                                    <!-- Dropdown Menu -->
                                    <div v-if="activeMenu === guide.id"
                                        class="absolute right-0 top-10 bg-white border border-gray-200 rounded-lg shadow-xl z-10 min-w-40 py-1">
                                        <button v-if="hasPermission('policies-processes-and-procedures-update')"
                                            @click="editGuide(guide)"
                                            class="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 flex items-center transition-colors">
                                            <font-awesome-icon icon="edit" class="mr-3 text-blue-500" />
                                            Edit
                                        </button>
                                        <button v-if="hasPermission('policies-processes-and-procedures-delete')"
                                            @click="deleteGuide(guide)"
                                            class="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center transition-colors">
                                            <font-awesome-icon icon="trash" class="mr-3" />
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- Document Info -->
                            <div class="mb-6 flex-1">
                                <h3 class="font-semibold text-gray-900 mb-3 line-clamp-2 text-lg leading-tight">{{
                                    guide.name }}</h3>
                                <p v-if="guide.description"
                                    class="text-sm text-gray-600 line-clamp-2 mb-3 leading-relaxed">
                                    {{ guide.description }}
                                </p>
                                <div class="flex items-center text-xs text-gray-500">
                                    <font-awesome-icon icon="user" class="mr-1" />
                                    <span>{{ guide.uploadedBy.name }}</span>
                                    <span class="mx-2">•</span>
                                    <font-awesome-icon icon="calendar" class="mr-1" />
                                    <span>{{ formatDate(guide.created_at) }}</span>
                                </div>
                            </div>

                            <!-- Actions -->
                            <div class="flex space-x-2">
                                <div v-if="hasPermission('policies-processes-and-procedures-view')"
                                    @click="viewGuide(guide)"
                                    class="flex-1 flex justify-center items-center bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white text-sm font-medium py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer">
                                    <font-awesome-icon icon="eye" class="mr-2" />
                                    View
                                </div>
                                <div v-if="hasPermission('policies-processes-and-procedures-download')"
                                    @click="downloadGuide(guide)"
                                    class="flex-1 flex justify-center items-center bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-sm font-medium py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer">
                                    <font-awesome-icon icon="download" class="mr-2" />
                                    Download
                                </div>
                                <div v-if="hasPermission('policies-processes-and-procedures-update')"
                                    @click="editGuide(guide)"
                                    class="bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white text-sm font-medium py-2.5 px-4 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer">
                                    <font-awesome-icon icon="edit" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Pagination -->
                    <div v-if="pagination.lastPage > 1" class="mt-8">
                        <pagination :paginatorInfo="pagination" :setPage="changePage"></pagination>
                    </div>
                </div>
            </div>
        </div>

        <!-- Upload Modal -->
        <base-modal v-if="showUploadModal" title="Upload Document" @close="showUploadModal = false">
            <guide-upload-modal @close="showUploadModal = false" @uploaded="onGuideUploaded" />
        </base-modal>

        <!-- Edit Modal -->
        <base-modal v-if="showEditModal" title="Edit Document" @close="showEditModal = false">
            <guide-edit-modal v-if="selectedGuide" :guide="selectedGuide" @close="showEditModal = false"
                @updated="onGuideUpdated" />
        </base-modal>

        <!-- Preview Modal -->
        <base-modal v-if="showPreviewModal" :title="previewModalTitle" @close="closePreviewModal" size="large">
            <div class="preview-container flex flex-col h-full">
                <!-- Loading State -->
                <div v-if="previewLoading" class="flex justify-center items-center h-full">
                    <base-spinner type="spin" class-list="h-12"></base-spinner>
                </div>

                <!-- Error State -->
                <div v-else-if="previewError" class="text-center py-8">
                    <font-awesome-icon icon="exclamation-triangle" class="text-red-500 text-4xl mb-4" />
                    <p class="text-red-600 mb-4">{{ previewError }}</p>
                    <base-button @click="downloadGuide(previewGuide)" class="bg-blue-600 hover:bg-blue-700 text-white">
                        <font-awesome-icon icon="download" class="mr-2" />
                        Download Instead
                    </base-button>
                </div>

                <!-- Preview Content -->
                <div v-else class="preview-content flex-1 overflow-y-auto">
                    <!-- PDF Preview -->
                    <div v-if="isPdfFile(previewGuide.file_type)" class="pdf-preview">
                        <iframe :src="previewUrl" class="w-full h-96 border border-gray-300 rounded-lg"
                            frameborder="0"></iframe>
                    </div>

                    <!-- Image Preview -->
                    <div v-else-if="isImageFile(previewGuide.file_type)" class="image-preview text-center">
                        <img :src="previewUrl" :alt="previewGuide.name"
                            class="max-w-full max-h-96 mx-auto rounded-lg shadow-lg" />
                    </div>

                    <!-- Text File Preview -->
                    <div v-else-if="isTextFile(previewGuide.file_type)" class="text-preview">
                        <iframe :src="previewUrl" class="w-full h-96 border border-gray-300 rounded-lg"
                            frameborder="0"></iframe>
                    </div>

                    <!-- DOCX and Office File Preview -->
                    <div v-else-if="isDocxFile(previewGuide.file_type) || isOfficeDocument(previewGuide.file_type)"
                        class="office-preview">
                        <!-- Microsoft Office Online Viewer (First Priority) -->
                        <div v-if="previewUrl && !useMammothFallback">
                            <iframe :src="previewUrl" class="w-full h-96 border border-gray-300 rounded-lg"
                                frameborder="0"></iframe>
                        </div>

                        <!-- Mammoth HTML Conversion (Fallback for DOCX) -->
                        <div v-else-if="useMammothFallback && previewHtml && isDocxFile(previewGuide.file_type)"
                            class="p-6">
                            <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                                <div class="flex items-start">
                                    <font-awesome-icon icon="info-circle"
                                        class="text-blue-500 text-lg mt-0.5 mr-3 flex-shrink-0" />
                                    <div class="text-sm text-blue-800">
                                        <p class="font-medium mb-1">Using HTML Preview</p>
                                        <p>Microsoft Office Online viewer is not available. Showing converted HTML
                                            preview. Some formatting may differ from the original document.</p>
                                    </div>
                                </div>
                            </div>
                            <div class="docx-wrapper mx-4">
                                <div v-html="previewHtml" class="docx-content prose max-w-none"></div>
                            </div>
                        </div>

                        <!-- Loading State -->
                        <div v-else class="text-center py-8">
                            <font-awesome-icon :icon="getFileIcon(previewGuide.file_type)"
                                :class="getFileIconColor(previewGuide.file_type)" class="text-4xl mb-4" />
                            <p class="text-gray-600">Loading document preview...</p>
                        </div>
                    </div>

                    <!-- Fallback for other file types -->
                    <div v-else class="fallback-preview text-center py-8">
                        <font-awesome-icon :icon="getFileIcon(previewGuide.file_type)"
                            :class="getFileIconColor(previewGuide.file_type)" class="text-6xl mb-4" />
                        <h3 class="text-lg font-semibold text-gray-900 mb-2">{{ previewGuide.name }}</h3>
                        <p class="text-gray-600 mb-4">
                            Preview not available for this file type. Please download to view the content.
                        </p>
                        <div class="flex justify-center space-x-4">
                            <base-button @click="downloadGuide(previewGuide)"
                                class="bg-blue-600 hover:bg-blue-700 text-white">
                                <font-awesome-icon icon="download" class="mr-2" />
                                Download File
                            </base-button>
                            <base-button @click="showPreviewModal = false"
                                class="bg-gray-600 hover:bg-gray-700 text-white">
                                Close
                            </base-button>
                        </div>
                    </div>
                </div>

                <!-- Modal Footer - Fixed at bottom -->
                <div v-if="!previewLoading && !previewError"
                    class="flex justify-end pt-4 pr-8 border-t border-gray-200 bg-white">
                    <base-button @click="downloadGuide(previewGuide)" class="bg-blue-600 hover:bg-blue-700 text-white">
                        <font-awesome-icon icon="download" class="mr-2" />
                        Download
                    </base-button>
                </div>
            </div>
        </base-modal>
    </div>
</template>

<script>
import { gql } from 'graphql-tag';
import mammoth from 'mammoth';
import Pagination from '../../components/shared/pagination.vue';
import BaseModal from '../../components/ui/BaseModal.vue';
import GuideUploadModal from './components/GuideUploadModal.vue';
import GuideEditModal from './components/GuideEditModal.vue';

export default {
    name: 'PolicyProcessesAndProcedures',
    components: {
        Pagination,
        BaseModal,
        GuideUploadModal,
        GuideEditModal
    },
    props: {
        hideBreadcrumb: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            guides: [],
            statistics: {},
            loading: false,
            searchQuery: '',
            selectedFileType: '',
            sortBy: 'created_at',
            sortOrder: 'desc',
            currentPage: 1,
            pagination: {
                currentPage: 1,
                lastPage: 1,
                perPage: 12,
                total: 0
            },
            showUploadModal: false,
            showEditModal: false,
            showPreviewModal: false,
            selectedGuide: null,
            previewGuide: null,
            previewUrl: null,
            previewHtml: null,
            previewLoading: false,
            previewError: null,
            useMammothFallback: false,
            activeMenu: null,
            searchTimeout: null,

        }
    },
    computed: {
        isSuperAdmin() {
            return this.$store.getters.isSuperAdmin;
        },
        emptyStateText() {
            if (this.hasPermission('policies-processes-and-procedures-create')) {
                return this.searchQuery || this.selectedFileType ? 'Try adjusting your search criteria.' : 'Get started by uploading your first document.';
            } else {
                return this.searchQuery || this.selectedFileType ? 'Try adjusting your search criteria.' : '';
            }
        },
        previewModalTitle() {
            return this.previewGuide ? `Preview: ${this.previewGuide.name}` : 'Preview Document';
        },
    },
    mounted() {
        this.fetchGuides()
        this.fetchStatistics()
        this.setupClickOutside()
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleClickOutside)
    },
    methods: {
        hasPermission(crudName) {
            if (this.isSuperAdmin) {
                return true;
            } else {
                const permissions = this.$store.getters.permissions;
                const permissionsArray = permissions.split(",");
                if (permissionsArray.includes(crudName)) {
                    return true;
                } else {
                    return false;
                }
            }
        },
        async fetchGuides() {
            this.loading = true
            try {
                const response = await this.$apollo.query({
                    query: gql`
            query GetGuides(
              $first: Int
              $page: Int
              $search: String
              $file_type: String
              $sort_by: String
              $sort_order: String
            ) {
              guides(
                first: $first
                page: $page
                search: $search
                file_type: $file_type
                sort_by: $sort_by
                sort_order: $sort_order
              ) {
                data {
                  id
                  name
                  original_filename
                  file_path
                  file_type
                  file_extension
                  file_size
                  formatted_file_size
                  description
                  download_url
                  created_at
                  updated_at
                  uploadedBy {
                    id
                    name
                  }
                }
                paginatorInfo {
                  count
                  currentPage
                  firstItem
                  hasMorePages
                  lastItem
                  lastPage
                  perPage
                  total
                }
              }
            }
          `,
                    variables: {
                        first: this.pagination.perPage,
                        page: this.currentPage,
                        search: this.searchQuery || null,
                        file_type: this.selectedFileType || null,
                        sort_by: this.sortBy,
                        sort_order: this.sortOrder
                    },
                    fetchPolicy: 'network-only'
                })

                if (response.data.guides.error) {
                    throw new Error(response.data.guides.message);
                }
                this.guides = response.data.guides.data
                this.pagination = response.data.guides.paginatorInfo
            } catch (error) {
                console.error('Error fetching guides:', error)
                console.error('Error details:', {
                    message: error.message,
                    graphQLErrors: error.graphQLErrors,
                    networkError: error.networkError
                });
                this.$swal({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to load documents. Please try again.'
                })
            } finally {
                this.loading = false
            }
        },

        async fetchStatistics() {
            try {
                const response = await this.$apollo.query({
                    query: gql`
            query GetGuideStatistics {
              guideStatistics {
                error
                error_code
                message
                data {
                  total_guides
                  total_size
                  formatted_total_size
                  file_types {
                    file_type
                    count
                  }
                }
              }
            }
          `,
                    fetchPolicy: 'network-only'
                })

                if (response.data.guideStatistics.error) {
                    throw new Error(response.data.guideStatistics.message);
                }
                this.statistics = response.data.guideStatistics.data
            } catch (error) {
                console.error('Error fetching statistics:', error)
            }
        },

        debounceSearch() {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = setTimeout(() => {
                this.currentPage = 1
                this.fetchGuides()
            }, 500)
        },

        changePage(page) {
            if (page >= 1 && page <= this.pagination.lastPage) {
                this.currentPage = page
                this.fetchGuides()
            }
        },



        getFileIcon(fileType) {
            const iconMap = {
                'application/pdf': 'file-pdf',
                'application/msword': 'file-word',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'file-word',
                'application/vnd.ms-excel': 'file-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'file-excel',
                'application/vnd.ms-powerpoint': 'file-powerpoint',
                'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'file-powerpoint',
                'text/plain': 'file-alt',
                'image/jpeg': 'file-image',
                'image/png': 'file-image',
                'image/gif': 'file-image'
            }
            return iconMap[fileType] || 'file'
        },

        getFileIconColor(fileType) {
            const colorMap = {
                'application/pdf': 'text-red-600',
                'application/msword': 'text-blue-600',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'text-blue-600',
                'application/vnd.ms-excel': 'text-green-600',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'text-green-600',
                'application/vnd.ms-powerpoint': 'text-orange-600',
                'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'text-orange-600',
                'text/plain': 'text-gray-600',
                'image/jpeg': 'text-purple-600',
                'image/png': 'text-purple-600',
                'image/gif': 'text-purple-600'
            }
            return colorMap[fileType] || 'text-gray-600'
        },

        getFileTypeCount(fileType) {
            if (!this.statistics.file_types) return 0
            const fileTypeData = this.statistics.file_types.find(ft => ft.file_type === fileType)
            return fileTypeData ? fileTypeData.count : 0
        },

        getWordFileCount() {
            return this.getFileTypeCount('application/msword') +
                this.getFileTypeCount('application/vnd.openxmlformats-officedocument.wordprocessingml.document')
        },

        isImageFile(fileType) {
            const imageTypes = [
                'image/jpeg',
                'image/jpg',
                'image/png',
                'image/gif',
                'image/bmp',
                'image/webp',
                'image/svg+xml',
                'image/tiff',
                'image/tif'
            ]
            return imageTypes.includes(fileType)
        },

        isPdfFile(fileType) {
            const pdfTypes = [
                'application/pdf'
            ]
            return pdfTypes.includes(fileType)
        },

        isTextFile(fileType) {
            const textTypes = [
                'text/plain',
                'text/html',
                'text/css',
                'text/javascript',
                'text/csv',
                'application/json',
                'application/xml',
                'text/xml'
            ]
            return textTypes.includes(fileType)
        },

        isOfficeDocument(fileType) {
            const officeTypes = [
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/vnd.ms-powerpoint',
                'application/vnd.openxmlformats-officedocument.presentationml.presentation'
            ]
            return officeTypes.includes(fileType)
        },

        isDocxFile(fileType) {
            const docxTypes = [
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            ]
            return docxTypes.includes(fileType)
        },

        async convertDocxToHtml(guideId) {
            try {
                // Get file content via GraphQL mutation
                const response = await this.$apollo.mutate({
                    mutation: gql`
            mutation GetGuideContent($id: ID!) {
              getGuideContent(id: $id) {
                error
                error_code
                message
                data {
                  content
                  file_type
                  guide {
                    id
                    name
                  }
                }
              }
            }
          `,
                    variables: {
                        id: guideId
                    }
                })

                if (response.data.getGuideContent.error) {
                    throw new Error(response.data.getGuideContent.message)
                }

                // Convert base64 content to ArrayBuffer
                const base64Content = response.data.getGuideContent.data.content
                const binaryString = atob(base64Content)
                const bytes = new Uint8Array(binaryString.length)
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i)
                }
                const arrayBuffer = bytes.buffer

                // Convert docx to HTML using mammoth with enhanced styling
                const result = await mammoth.convertToHtml({
                    arrayBuffer: arrayBuffer,
                    styleMap: [
                        "p[style-name='Heading 1'] => h1:fresh",
                        "p[style-name='Heading 2'] => h2:fresh",
                        "p[style-name='Heading 3'] => h3:fresh",
                        "p[style-name='Title'] => h1.title:fresh",
                        "p[style-name='Subtitle'] => h2.subtitle:fresh",
                        "p[style-name='TOC Heading'] => h2.toc-heading:fresh",
                        "p[style-name='toc 1'] => p.toc-1:fresh",
                        "p[style-name='Default'] => p:fresh",
                        "p[style-name='Normal (Web)'] => p:fresh",
                        "p[style-name='List Paragraph'] => p.list-paragraph:fresh",
                        "r[style-name='Strong'] => strong",
                        "r[style-name='Emphasis'] => em"
                    ],
                    includeEmbeddedStyleMap: true,
                    includeDefaultStyleMap: true,
                    preserveStyles: true,
                    convertImage: mammoth.images.imgElement(function (image) {
                        // Handle EMF images which are not supported by browsers
                        if (image.contentType === 'image/x-emf' || image.contentType === 'image/emf') {
                            return {
                                src: "data:image/svg+xml;base64," + btoa(`
                  <svg width="100" height="50" xmlns="http://www.w3.org/2000/svg">
                    <rect width="100" height="50" fill="#f0f0f0" stroke="#ccc"/>
                    <text x="50" y="30" text-anchor="middle" font-family="Arial" font-size="10" fill="#666">Logo</text>
                  </svg>
                `),
                                style: "max-width: 100%; height: auto; display: block; margin: 0 auto; border: 1px solid #ccc;"
                            };
                        }

                        return image.read("base64").then(function (imageBuffer) {
                            return {
                                src: "data:" + image.contentType + ";base64," + imageBuffer,
                                style: "max-width: 100%; height: auto; display: block; margin: 0 auto;"
                            };
                        });
                    })
                })

                return {
                    html: result.value,
                    messages: result.messages
                }
            } catch (error) {
                console.error('Error converting docx to HTML:', error)
                throw new Error('Failed to convert document to HTML: ' + error.message)
            }
        },


        formatDate(dateString) {
            const date = new Date(dateString)
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            })
        },

        showGuideMenu(guideId) {
            this.activeMenu = this.activeMenu === guideId ? null : guideId
        },

        setupClickOutside() {
            document.addEventListener('click', this.handleClickOutside)
        },

        handleClickOutside(event) {
            if (!event.target.closest('.relative')) {
                this.activeMenu = null
            }
        },

        closePreviewModal() {
            this.showPreviewModal = false
            this.previewGuide = null
            this.previewUrl = null
            this.previewHtml = null
            this.previewError = null
            this.useMammothFallback = false
        },

        async forceDownload(guideId, filename) {
            try {
                // Get file content via GraphQL mutation to avoid CORS issues
                const response = await this.$apollo.mutate({
                    mutation: gql`
            mutation GetGuideContent($id: ID!) {
              getGuideContent(id: $id) {
                error
                error_code
                message
                data {
                  content
                  file_type
                  guide {
                    id
                    name
                  }
                }
              }
            }
          `,
                    variables: {
                        id: guideId
                    }
                })

                if (response.data.getGuideContent.error) {
                    throw new Error(response.data.getGuideContent.message)
                }

                // Convert base64 content to blob
                const base64Content = response.data.getGuideContent.data.content
                const binaryString = atob(base64Content)
                const bytes = new Uint8Array(binaryString.length)
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i)
                }

                // Create blob from binary data
                const blob = new Blob([bytes], { type: response.data.getGuideContent.data.file_type })

                // Create object URL from blob
                const blobUrl = window.URL.createObjectURL(blob)

                // Create download link
                const link = document.createElement('a')
                link.href = blobUrl
                link.download = filename
                link.style.display = 'none'

                // Add to DOM, click, and remove
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)

                // Clean up object URL
                window.URL.revokeObjectURL(blobUrl)

            } catch (error) {
                console.error('Error downloading file:', error)
                this.$swal({
                    icon: 'error',
                    title: 'Download Failed',
                    text: 'Failed to download the file. Please try again.'
                })
            }
        },

        async viewGuide(guide) {
            this.previewGuide = guide
            this.previewLoading = true
            this.previewError = null
            this.previewUrl = null
            this.previewHtml = null
            this.useMammothFallback = false
            this.showPreviewModal = true

            try {
                const response = await this.$apollo.mutate({
                    mutation: gql`
            mutation DownloadGuide($id: ID!) {
              downloadGuide(id: $id) {
                error
                error_code
                message
                data {
                  download_url
                  guide {
                    id
                    name
                  }
                }
              }
            }
          `,
                    variables: {
                        id: guide.id
                    }
                })

                if (!response.data.downloadGuide.error) {
                    const downloadUrl = response.data.downloadGuide.data.download_url

                    // Handle DOCX and other Office files
                    if (this.isDocxFile(guide.file_type) || this.isOfficeDocument(guide.file_type)) {
                        // First priority: Try Microsoft Office Online viewer
                        try {
                            const encodedUrl = encodeURIComponent(downloadUrl)
                            const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`

                            // Set the Office Online viewer URL
                            // We'll only fall back to mammoth if there's an actual error loading the iframe
                            this.previewUrl = officeViewerUrl
                        } catch (officeError) {
                            console.error('Error setting up Office Online viewer:', officeError)
                            // Fall back to mammoth for DOCX files only if Office Online setup fails
                            if (this.isDocxFile(guide.file_type)) {
                                try {
                                    const conversionResult = await this.convertDocxToHtml(guide.id)
                                    this.previewHtml = conversionResult.html
                                    this.useMammothFallback = true
                                    this.previewUrl = null

                                    if (conversionResult.messages && conversionResult.messages.length > 0) {
                                        console.warn('DOCX conversion messages:', conversionResult.messages)
                                    }
                                } catch (conversionError) {
                                    console.error('Error converting DOCX:', conversionError)
                                    this.previewError = 'Failed to load document preview. Please download the file instead.'
                                }
                            } else {
                                // For other Office files, just use the download URL directly
                                this.previewUrl = downloadUrl
                            }
                        }
                    } else {
                        // For other file types, use the URL directly
                        this.previewUrl = downloadUrl
                    }
                } else {
                    this.previewError = response.data.downloadGuide.message
                }
            } catch (error) {
                console.error('Error loading guide for preview:', error)
                this.previewError = 'Failed to load the guide for preview. Please try again.'
            } finally {
                this.previewLoading = false
            }
        },


        async downloadGuide(guide) {
            try {
                // Force download for all file types using guide ID
                await this.forceDownload(guide.id, guide.original_filename)
            } catch (error) {
                console.error('Error downloading guide:', error)
                this.$swal({
                    icon: 'error',
                    title: 'Download Failed',
                    text: 'Failed to download the document. Please try again.'
                })
            }
        },

        editGuide(guide) {
            this.selectedGuide = guide
            this.showEditModal = true
            this.activeMenu = null
        },

        async deleteGuide(guide) {
            const result = await this.$swal({
                title: 'Delete Document',
                text: `Are you sure you want to delete "${guide.name}"? This action cannot be undone.`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Yes, delete it!'
            })

            if (result.isConfirmed) {
                try {
                    const response = await this.$apollo.mutate({
                        mutation: gql`
              mutation DeleteGuide($id: ID!) {
                deleteGuide(id: $id) {
                  error
                  error_code
                  message
                  data {
                    id
                  }
                }
              }
            `,
                        variables: {
                            id: guide.id
                        }
                    })

                    if (!response.data.deleteGuide.error) {
                        this.$swal({
                            icon: 'success',
                            title: 'Deleted!',
                            text: response.data.deleteGuide.message
                        })

                        await this.fetchGuides()
                        await this.fetchStatistics()
                    } else {
                        this.$swal({
                            icon: 'error',
                            title: 'Delete Failed',
                            text: response.data.deleteGuide.message
                        })
                    }
                } catch (error) {
                    console.error('Error deleting guide:', error)
                    this.$swal({
                        icon: 'error',
                        title: 'Delete Failed',
                        text: 'Failed to delete the document. Please try again.'
                    })
                }
            }
            this.activeMenu = null
        },

        async onGuideUploaded() {
            this.showUploadModal = false

            // Reset to first page to show the newly uploaded guide
            this.currentPage = 1

            try {
                this.loading = true

                // Fetch fresh data
                await this.fetchGuides()
                await this.fetchStatistics()

                this.$swal({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Document uploaded successfully.'
                })
            } catch (error) {
                console.error('Error refreshing data after upload:', error)
                this.$swal({
                    icon: 'error',
                    title: 'Upload Successful',
                    text: 'Document uploaded but there was an issue refreshing the data. Please refresh the page.'
                })
            } finally {
                this.loading = false
            }
        },

        async onGuideUpdated() {
            this.showEditModal = false

            try {
                this.loading = true

                // Fetch fresh data
                await this.fetchGuides()
                await this.fetchStatistics()

                this.$swal({
                    icon: 'success',
                    title: 'Updated!',
                    text: 'Document updated successfully.'
                })
            } catch (error) {
                console.error('Error refreshing data after update:', error)
                this.$swal({
                    icon: 'error',
                    title: 'Update Successful',
                    text: 'Document updated but there was an issue refreshing the data. Please refresh the page.'
                })
            } finally {
                this.loading = false
            }
        }
    }
}
</script>

<style scoped>
.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.preview-container {
    height: 70vh;
    display: flex;
    flex-direction: column;
}

.preview-content {
    min-height: 200px;
}

.pdf-preview iframe,
.text-preview iframe,
.office-preview iframe {
    min-height: 70vh;
}

.image-preview img {
    max-height: 60vh;
    object-fit: contain;
}

.docx-wrapper {
    background: white;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
    padding: 24px;
    margin: 0 auto;
    max-width: 800px;
    min-height: 400px;
}

.docx-content {
    font-family: 'Times New Roman', Times, serif;
    line-height: 1.6;
    color: #000;
    font-size: 12pt;
    max-width: 100%;
}

.docx-content h1,
.docx-content h2,
.docx-content h3,
.docx-content h4,
.docx-content h5,
.docx-content h6 {
    margin-top: 1.5em;
    margin-bottom: 0.5em;
    font-weight: bold;
    color: #000;
    text-align: center;
}

.docx-content h1.title {
    font-size: 16pt;
    font-weight: bold;
    text-align: center;
    margin: 1em 0;
}

.docx-content p {
    margin-bottom: 0.5em;
    text-align: center;
}

.docx-content strong {
    font-weight: bold;
}

.docx-content em {
    font-style: italic;
}

.docx-content ul,
.docx-content ol {
    margin-bottom: 1em;
    padding-left: 2em;
}

.docx-content table {
    border-collapse: collapse;
    width: 100%;
    margin-bottom: 1em;
    border: 1px solid #000;
}

.docx-content table th,
.docx-content table td {
    border: 1px solid #000;
    padding: 8px;
    text-align: center;
    vertical-align: middle;
    background-color: #fff;
}

.docx-content table th {
    background-color: #f0f0f0;
    font-weight: bold;
}

.docx-content img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 0 auto;
}

/* Specific styling for memorandum documents */
.docx-content p[style*="text-align: center"] {
    text-align: center;
}

.docx-content p[style*="font-weight: bold"] {
    font-weight: bold;
}

.docx-content p[style*="font-size"] {
    font-size: inherit;
}

/* Handle Word table borders and styling */
.docx-content table[style*="border"] {
    border: 1px solid #000;
}

.docx-content td[style*="border"] {
    border: 1px solid #000;
}

/* Preserve Word document spacing */
.docx-content br {
    line-height: 1.2;
}

/* Handle highlighted text (yellow background) */
.docx-content span[style*="background-color: yellow"],
.docx-content span[style*="background-color: #ffff00"] {
    background-color: #ffff00;
    padding: 2px 4px;
}

/* TOC and List Paragraph styles */
.docx-content .toc-heading {
    font-weight: bold;
    margin-top: 1em;
    margin-bottom: 0.5em;
}

.docx-content .toc-1 {
    margin-left: 1em;
    margin-bottom: 0.25em;
}

.docx-content .list-paragraph {
    margin-left: 1em;
    margin-bottom: 0.5em;
}

/* Default and Normal Web styles */
.docx-content p[class*="default"],
.docx-content p[class*="normal"] {
    margin-bottom: 0.5em;
}
</style>
